import Add from '@mui/icons-material/Add';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import {
  getDashboardErrorMessage,
  useCreateDashboardWidgetMutation,
  useDeleteDashboardWidgetMutation,
  useReorderDashboardWidgetsMutation,
  useReplaceDashboardWidgetMetricsMutation,
  useUpdateDashboardWidgetMutation
} from './dashboardApi';
import DashboardColourPicker from './DashboardColourPicker';
import {
  DASHBOARD_WIDGET_TARGET_MODE,
  DASHBOARD_WIDGET_TYPE
} from './dashboardContract';
import {
  canChangeDashboardWidgetType,
  changeDashboardWidgetType,
  dashboardWidgetToForm,
  DASHBOARD_WIDGET_MAX_COUNT,
  DASHBOARD_WIDGET_MAX_METRICS,
  DASHBOARD_WIDGET_TARGET_MODE_OPTIONS,
  DASHBOARD_WIDGET_TYPE_OPTIONS,
  EMPTY_DASHBOARD_WIDGET_FORM,
  getActiveDashboardWidgetMetrics,
  isDashboardWidgetMetricSelectable,
  isSingleMetricDashboardWidget,
  moveDashboardWidget,
  moveDashboardWidgetMetric,
  sortDashboardWidgets,
  toggleDashboardWidgetMetric,
  validateDashboardWidget
} from './dashboardWidgetModel';

const widgetTypeLabel = (value) =>
  DASHBOARD_WIDGET_TYPE_OPTIONS.find((option) => option.value === value)
    ?.label ?? value;

const WidgetMetricFields = ({
  activeMetrics,
  errors,
  form,
  onMetricChange,
  onMove,
  onToggleMetric
}) => {
  const metricById = useMemo(
    () =>
      new Map(
        activeMetrics.map((metric) => [metric.metricDefinitionId, metric])
      ),
    [activeMetrics]
  );
  const singleMetric = isSingleMetricDashboardWidget(form.widgetType);
  const minimum = singleMetric ? 1 : 2;
  const maximum = singleMetric ? 1 : DASHBOARD_WIDGET_MAX_METRICS;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography fontWeight={700}>Widget metrics</Typography>
        <Typography color="text.secondary" variant="body2">
          {singleMetric
            ? 'Choose one active metric. Selecting another replaces the current choice.'
            : 'Choose two to six active metrics.'}
        </Typography>
      </Box>

      {activeMetrics.length ? (
        <FormControl component="fieldset" error={Boolean(errors.metrics)}>
          <FormGroup
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))'
              }
            }}
          >
            {activeMetrics.map((metric) => {
              const checked = form.metrics.some(
                (item) => item.metricDefinitionId === metric.metricDefinitionId
              );
              const selectable = isDashboardWidgetMetricSelectable(
                form,
                metric
              );
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      disabled={!selectable}
                      onChange={(event) =>
                        onToggleMetric(
                          metric.metricDefinitionId,
                          event.target.checked
                        )
                      }
                    />
                  }
                  key={metric.metricDefinitionId}
                  label={metric.label}
                />
              );
            })}
          </FormGroup>
          <FormHelperText>
            {errors.metrics ||
              `${form.metrics.length} selected · ${minimum === maximum ? '1 required' : `${minimum}-${maximum} required`}`}
          </FormHelperText>
        </FormControl>
      ) : (
        <Alert severity="warning">
          Add or restore an active metric before adding widgets.
        </Alert>
      )}

      {form.metrics.length > 0 && (
        <Stack spacing={1.25}>
          <Typography fontWeight={700} variant="body2">
            Selected metrics
          </Typography>
          {form.metrics.map((item, index) => {
            const metric = metricById.get(item.metricDefinitionId);
            const label = metric?.label ?? 'Unavailable metric';
            return (
              <Paper
                elevation={0}
                key={item.metricDefinitionId}
                sx={{ border: '1px solid', borderColor: 'divider', p: 1.5 }}
              >
                <Stack
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                >
                  <Box sx={{ flex: 1, minWidth: 150 }}>
                    <Typography fontWeight={700} variant="body2">
                      {label}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {metric?.valueType ?? 'Unknown type'}
                      {metric?.unitLabel ? ` · ${metric.unitLabel}` : ''}
                    </Typography>
                  </Box>
                  <DashboardColourPicker
                    emptyLabel="Use metric colour"
                    error={Boolean(errors[`metrics.${index}.colour`])}
                    fallbackColour={metric?.colour}
                    helperText={
                      errors[`metrics.${index}.colour`] ||
                      'Blank uses metric colour.'
                    }
                    label={`${label} colour`}
                    onChange={(value) => onMetricChange(index, 'colour', value)}
                    size="small"
                    sx={{ minWidth: 190 }}
                    value={item.colour}
                  />
                  <Stack direction="row" spacing={0.25}>
                    {!singleMetric && (
                      <>
                        <Tooltip title="Move metric up">
                          <span>
                            <IconButton
                              aria-label={`Move ${label} metric up`}
                              disabled={index === 0}
                              onClick={() =>
                                onMove(item.metricDefinitionId, -1)
                              }
                              size="small"
                            >
                              <ArrowUpward fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move metric down">
                          <span>
                            <IconButton
                              aria-label={`Move ${label} metric down`}
                              disabled={index === form.metrics.length - 1}
                              onClick={() => onMove(item.metricDefinitionId, 1)}
                              size="small"
                            >
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    <Button
                      color="error"
                      onClick={() =>
                        onToggleMetric(item.metricDefinitionId, false)
                      }
                      size="small"
                    >
                      Remove
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

const WidgetFields = ({
  activeMetrics,
  editingType,
  errors,
  form,
  onChange,
  onMetricChange,
  onMoveMetric,
  onToggleMetric,
  onTypeChange
}) => {
  const progress = form.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS;
  const distribution =
    form.widgetType === DASHBOARD_WIDGET_TYPE.PIE ||
    form.widgetType === DASHBOARD_WIDGET_TYPE.DOUGHNUT;

  return (
    <Stack spacing={2.5}>
      <TextField
        autoFocus
        error={Boolean(errors.name)}
        fullWidth
        helperText={errors.name || `${form.name.length}/100 characters`}
        inputProps={{ maxLength: 101 }}
        label="Widget name"
        onChange={(event) => onChange('name', event.target.value)}
        required
        value={form.name}
      />
      <TextField
        error={Boolean(errors.description)}
        fullWidth
        helperText={
          errors.description || `${form.description.length}/500 characters`
        }
        inputProps={{ maxLength: 501 }}
        label="Description"
        minRows={2}
        multiline
        onChange={(event) => onChange('description', event.target.value)}
        value={form.description}
      />
      <TextField
        error={Boolean(errors.widgetType)}
        helperText={
          errors.widgetType ||
          (editingType
            ? 'To switch between a single-metric widget and a chart, remove this widget and add a new one.'
            : DASHBOARD_WIDGET_TYPE_OPTIONS.find(
                (option) => option.value === form.widgetType
              )?.description)
        }
        label="Widget type"
        onChange={(event) => onTypeChange(event.target.value)}
        required
        select
        value={form.widgetType}
      >
        {DASHBOARD_WIDGET_TYPE_OPTIONS.map((option) => (
          <MenuItem
            disabled={
              Boolean(editingType) &&
              !canChangeDashboardWidgetType(editingType, option.value)
            }
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {progress && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
          }}
        >
          <TextField
            error={Boolean(
              errors.targetMode ||
                (form.targetMode ===
                  DASHBOARD_WIDGET_TARGET_MODE.METRIC_MAXIMUM &&
                  errors.targetValue)
            )}
            helperText={
              errors.targetMode ||
              (form.targetMode === DASHBOARD_WIDGET_TARGET_MODE.METRIC_MAXIMUM
                ? errors.targetValue ||
                  'Uses the maximum configured on the selected metric.'
                : 'Choose where the progress target comes from.')
            }
            label="Progress target"
            onChange={(event) => onChange('targetMode', event.target.value)}
            select
            value={form.targetMode}
          >
            {DASHBOARD_WIDGET_TARGET_MODE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {form.targetMode === DASHBOARD_WIDGET_TARGET_MODE.CUSTOM && (
            <TextField
              error={Boolean(errors.targetValue)}
              helperText={errors.targetValue || 'Must be greater than zero.'}
              inputProps={{ step: 'any' }}
              label="Custom target"
              onChange={(event) => onChange('targetValue', event.target.value)}
              type="number"
              value={form.targetValue}
            />
          )}
          <TextField
            error={Boolean(errors.warningThreshold)}
            helperText={
              errors.warningThreshold ||
              'Progress below this percentage is red.'
            }
            inputProps={{ min: 0, max: 100, step: 'any' }}
            label="Red below (%)"
            onChange={(event) =>
              onChange('warningThreshold', event.target.value)
            }
            type="number"
            value={form.warningThreshold}
          />
          <TextField
            error={Boolean(errors.warningThreshold)}
            helperText="Progress at or above this percentage is green; values between are yellow."
            inputProps={{ min: 0, max: 100, step: 'any' }}
            label="Green from (%)"
            onChange={(event) =>
              onChange('successThreshold', event.target.value)
            }
            type="number"
            value={form.successThreshold}
          />
        </Box>
      )}

      {distribution && (
        <FormControlLabel
          control={
            <Checkbox
              checked={form.showLegend}
              onChange={(event) => onChange('showLegend', event.target.checked)}
            />
          }
          label="Show chart legend"
        />
      )}

      <WidgetMetricFields
        activeMetrics={activeMetrics}
        errors={errors}
        form={form}
        onMetricChange={onMetricChange}
        onMove={onMoveMetric}
        onToggleMetric={onToggleMetric}
      />
    </Stack>
  );
};

const WidgetRow = ({
  canEdit,
  disableMoveDown,
  disableMoveUp,
  isBusy,
  onDelete,
  onEdit,
  onMoveDown,
  onMoveUp,
  widget
}) => {
  const metrics = [...(widget.metrics ?? [])].sort(
    (left, right) => left.seriesOrder - right.seriesOrder
  );
  return (
    <Paper
      data-testid={`dashboard-widget-definition-${widget.dashboardWidgetId}`}
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', md: 'center' }}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography fontWeight={700}>{widget.name}</Typography>
          {widget.description && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
              {widget.description}
            </Typography>
          )}
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
            <Chip label={widgetTypeLabel(widget.widgetType)} size="small" />
            <Chip
              label={`${metrics.length} ${metrics.length === 1 ? 'metric' : 'metrics'}`}
              size="small"
              variant="outlined"
            />
            {widget.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS && (
              <Chip
                label={
                  widget.targetMode === DASHBOARD_WIDGET_TARGET_MODE.CUSTOM
                    ? `Target ${widget.targetValue}`
                    : 'Metric maximum target'
                }
                size="small"
                variant="outlined"
              />
            )}
            {(widget.widgetType === DASHBOARD_WIDGET_TYPE.PIE ||
              widget.widgetType === DASHBOARD_WIDGET_TYPE.DOUGHNUT) && (
              <Chip
                label={widget.showLegend ? 'Legend shown' : 'Legend hidden'}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
          {metrics.length > 0 && (
            <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
              {metrics.map((metric) => metric.label).join(' · ')}
            </Typography>
          )}
        </Box>
        {canEdit && (
          <Stack alignItems="center" direction="row" flexWrap="wrap" gap={0.5}>
            <Tooltip title="Move widget up">
              <span>
                <IconButton
                  aria-label={`Move ${widget.name} up`}
                  disabled={disableMoveUp || isBusy}
                  onClick={onMoveUp}
                  size="small"
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move widget down">
              <span>
                <IconButton
                  aria-label={`Move ${widget.name} down`}
                  disabled={disableMoveDown || isBusy}
                  onClick={onMoveDown}
                  size="small"
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              disabled={isBusy}
              onClick={onEdit}
              size="small"
              startIcon={<EditOutlined />}
            >
              Edit
            </Button>
            <Button
              color="error"
              disabled={isBusy}
              onClick={onDelete}
              size="small"
              startIcon={<DeleteOutline />}
            >
              Remove
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const DashboardWidgetDesigner = ({
  canEdit,
  dashboardTemplateId,
  draftVersion
}) => {
  const activeMetrics = getActiveDashboardWidgetMetrics(draftVersion?.metrics);
  const widgets = sortDashboardWidgets(draftVersion?.widgets);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [deleteWidget, setDeleteWidget] = useState(null);
  const [form, setForm] = useState({
    ...EMPTY_DASHBOARD_WIDGET_FORM,
    metrics: []
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);
  const [createWidget, createState] = useCreateDashboardWidgetMutation();
  const [updateWidget, updateState] = useUpdateDashboardWidgetMutation();
  const [replaceMetrics, metricsState] =
    useReplaceDashboardWidgetMetricsMutation();
  const [removeWidget, deleteState] = useDeleteDashboardWidgetMutation();
  const [reorderWidgets, reorderState] = useReorderDashboardWidgetsMutation();
  const editorBusy =
    createState.isLoading || updateState.isLoading || metricsState.isLoading;
  const isBusy = editorBusy || deleteState.isLoading || reorderState.isLoading;
  const actionError = deleteState.error || reorderState.error;
  const atWidgetLimit = widgets.length >= DASHBOARD_WIDGET_MAX_COUNT;

  const clearActionStates = () => {
    deleteState.reset?.();
    reorderState.reset?.();
    setSuccessMessage(null);
  };

  const openCreate = () => {
    createState.reset?.();
    updateState.reset?.();
    metricsState.reset?.();
    setSelectedWidget(null);
    setForm({ ...EMPTY_DASHBOARD_WIDGET_FORM, metrics: [] });
    setErrors({});
    setSuccessMessage(null);
    setEditorOpen(true);
  };

  const openEdit = (widget) => {
    createState.reset?.();
    updateState.reset?.();
    metricsState.reset?.();
    setSelectedWidget(widget);
    setForm(dashboardWidgetToForm(widget));
    setErrors({});
    setSuccessMessage(null);
    setEditorOpen(true);
  };

  const clearFieldError = (field) => {
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const setFormValue = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    clearFieldError(field);
    if (field === 'successThreshold') clearFieldError('warningThreshold');
  };

  const setWidgetType = (widgetType) => {
    setForm((current) => changeDashboardWidgetType(current, widgetType));
    setErrors({});
  };

  const toggleMetric = (metricDefinitionId, selected) => {
    setForm((current) => ({
      ...current,
      metrics: toggleDashboardWidgetMetric(
        current.metrics,
        metricDefinitionId,
        selected,
        current.widgetType
      )
    }));
    clearFieldError('metrics');
    clearFieldError('targetValue');
  };

  const setMetricValue = (index, field, value) => {
    setForm((current) => ({
      ...current,
      metrics: current.metrics.map((metric, metricIndex) =>
        metricIndex === index ? { ...metric, [field]: value } : metric
      )
    }));
    clearFieldError(`metrics.${index}.${field}`);
  };

  const moveMetric = (metricDefinitionId, direction) => {
    setForm((current) => ({
      ...current,
      metrics: moveDashboardWidgetMetric(
        current.metrics,
        metricDefinitionId,
        direction
      )
    }));
  };

  const submitWidget = async (event) => {
    event.preventDefault();
    const validation = validateDashboardWidget(form, activeMetrics);
    setErrors(validation.errors);
    if (!validation.isValid) return;
    try {
      if (selectedWidget) {
        const identifiers = {
          dashboardTemplateId,
          dashboardWidgetId: selectedWidget.dashboardWidgetId
        };
        await updateWidget({
          ...identifiers,
          ...validation.values.configuration
        }).unwrap();
        await replaceMetrics({
          ...identifiers,
          metrics: validation.values.metrics
        }).unwrap();
        setSuccessMessage(`${validation.values.configuration.name} updated.`);
      } else {
        await createWidget({
          dashboardTemplateId,
          ...validation.values.configuration,
          metrics: validation.values.metrics
        }).unwrap();
        setSuccessMessage(`${validation.values.configuration.name} added.`);
      }
      setEditorOpen(false);
    } catch (_error) {
      // RTK Query exposes normalized errors through the mutation states.
    }
  };

  const moveWidget = async (dashboardWidgetId, direction) => {
    clearActionStates();
    try {
      await reorderWidgets({
        dashboardTemplateId,
        dashboardWidgetIds: moveDashboardWidget(
          widgets,
          dashboardWidgetId,
          direction
        )
      }).unwrap();
      setSuccessMessage('Widget order updated.');
    } catch (_error) {
      // RTK Query exposes the normalized error through reorderState.
    }
  };

  const confirmDelete = async () => {
    if (!deleteWidget) return;
    clearActionStates();
    try {
      await removeWidget({
        dashboardTemplateId,
        dashboardWidgetId: deleteWidget.dashboardWidgetId
      }).unwrap();
      setSuccessMessage(`${deleteWidget.name} removed.`);
      setDeleteWidget(null);
    } catch (_error) {
      // Keep the confirmation open so the API error remains visible.
    }
  };

  const addDisabled = !activeMetrics.length || atWidgetLimit;
  const addTooltip = !activeMetrics.length
    ? 'Add or restore an active metric first.'
    : atWidgetLimit
      ? `A dashboard can contain up to ${DASHBOARD_WIDGET_MAX_COUNT} widgets.`
      : '';

  return (
    <Box data-testid="dashboard-widget-designer" sx={{ mt: 4 }}>
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography component="h2" fontSize={22} fontWeight={700}>
            Dashboard widgets
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            Configure up to five numeric, progress, pie, or doughnut widgets for
            the top of the dashboard.
          </Typography>
        </Box>
        {canEdit && (
          <Tooltip title={addTooltip}>
            <span>
              <Button
                disabled={addDisabled}
                onClick={openCreate}
                startIcon={<Add />}
                variant="contained"
              >
                Add widget
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Typography color="text.secondary" sx={{ mt: 1.5 }} variant="body2">
        {widgets.length} of {DASHBOARD_WIDGET_MAX_COUNT} widgets ·{' '}
        {activeMetrics.length} active{' '}
        {activeMetrics.length === 1 ? 'metric' : 'metrics'} available
      </Typography>

      {!canEdit && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You can view this widget configuration, but you do not have permission
          to change it.
        </Alert>
      )}
      {!activeMetrics.length && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Widgets need active metrics. Add or restore a metric before creating a
          widget.
        </Alert>
      )}
      {atWidgetLimit && (
        <Alert severity="info" sx={{ mt: 2 }}>
          This draft has the maximum of {DASHBOARD_WIDGET_MAX_COUNT} widgets.
          Remove one before adding another.
        </Alert>
      )}
      {successMessage && (
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ mt: 2 }}
        >
          {successMessage}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(actionError)}
        </Alert>
      )}

      {widgets.length ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {widgets.map((widget, index) => (
            <WidgetRow
              canEdit={canEdit}
              disableMoveDown={index === widgets.length - 1}
              disableMoveUp={index === 0}
              isBusy={isBusy}
              key={widget.dashboardWidgetId}
              onDelete={() => {
                clearActionStates();
                setDeleteWidget(widget);
              }}
              onEdit={() => openEdit(widget)}
              onMoveDown={() => moveWidget(widget.dashboardWidgetId, 1)}
              onMoveUp={() => moveWidget(widget.dashboardWidgetId, -1)}
              widget={widget}
            />
          ))}
        </Stack>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            mt: 2,
            p: 3,
            textAlign: 'center'
          }}
        >
          <Typography fontWeight={700}>No widgets in this draft</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            Add a widget to highlight the latest approved dashboard values.
          </Typography>
          {canEdit && (
            <Button
              disabled={addDisabled}
              onClick={openCreate}
              sx={{ mt: 2 }}
              variant="outlined"
            >
              Add first widget
            </Button>
          )}
        </Paper>
      )}

      <Dialog
        fullWidth
        maxWidth="md"
        onClose={() => !editorBusy && setEditorOpen(false)}
        open={editorOpen}
      >
        <Box component="form" noValidate onSubmit={submitWidget}>
          <DialogTitle>
            {selectedWidget ? 'Edit widget' : 'Add widget'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <WidgetFields
                activeMetrics={activeMetrics}
                editingType={selectedWidget?.widgetType ?? null}
                errors={errors}
                form={form}
                onChange={setFormValue}
                onMetricChange={setMetricValue}
                onMoveMetric={moveMetric}
                onToggleMetric={toggleMetric}
                onTypeChange={setWidgetType}
              />
              {(createState.error ||
                updateState.error ||
                metricsState.error) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {getDashboardErrorMessage(
                    createState.error || updateState.error || metricsState.error
                  )}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button disabled={editorBusy} onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button disabled={editorBusy} type="submit" variant="contained">
              {editorBusy
                ? 'Saving…'
                : selectedWidget
                  ? 'Save widget'
                  : 'Add widget'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        maxWidth="sm"
        onClose={() => !deleteState.isLoading && setDeleteWidget(null)}
        open={Boolean(deleteWidget)}
      >
        <DialogTitle>Remove {deleteWidget?.name}?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            This removes the widget from the current draft. It does not delete
            metric data, and the published dashboard remains unchanged until
            this draft is published.
          </Alert>
          {deleteState.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {getDashboardErrorMessage(deleteState.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleteState.isLoading}
            onClick={() => setDeleteWidget(null)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deleteState.isLoading}
            onClick={confirmDelete}
            variant="contained"
          >
            {deleteState.isLoading ? 'Removing…' : 'Remove widget'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardWidgetDesigner;
